import React, { useState, useEffect } from 'react';
import { ArrowRight, Check, Info } from 'lucide-react';
import toast from 'react-hot-toast';
import {
  Button, Modal, Select, WeekPatternGrid, StaffAccessPicker,
  defaultWeekPattern, weeklyPaidHours,
} from '../../../components';
import recruitmentService from '../../../services/carehome_manager/recruitmentService';
import { cardCls, inputCls, fieldLabelCls, Pill, toDateInput } from './shared';
import { JOB_TITLES } from '../../../utils/jobTitles';
import ManagerChecksSection from './ManagerChecksSection';
import { useAuth } from '../../../context/AuthContext';
import { isOwnerRole } from '../../../config/menuCodeMap';
import HistorySection from './HistorySection';

/**
 * Step 4 — Transfer to Staff Management.
 *
 * Carries the candidate's answers across once and hands the record to Staff
 * Management. It asks for exactly two things, because they are the two a staff
 * record cannot exist without: WHEN THEY WORK and WHERE THEY CAN SIGN IN. Both
 * use the same components and the same payload as the Add Staff form, so a
 * transferred record and a typed one are indistinguishable afterwards.
 *
 * Everything else about the job — title, contract, pay, induction — is finished
 * in Staff Management against the record this creates. Keeping a second copy of
 * the Add Staff form here would be two screens to maintain and two places for
 * the same answer to disagree.
 *
 * Two things this screen is careful NOT to say:
 *
 *   It does not say the checks passed. The gate is the three forms being back,
 *   not the compliance verdicts — a DBS can take weeks, and a manager who
 *   cannot move a candidate until it lands is stuck. Undecided checks stay
 *   visible below and in Staff Compliance.
 *
 *   It does not create an active member of staff. The record lands as setup
 *   pending with no PIN, so nobody can sign in until a person finishes the job.
 */
const TransferPanel = ({
  candidate, definitions, busy, locked, run, availableMenus, loadingMenus, domiciliary, groups = [],
  onTransferred,
}) => {
  const { user, sites, activeSiteId } = useAuth();
  // Only the account owner may transfer a candidate in as a manager.
  const isOwner = isOwnerRole(user);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(null);
  // What the transfer produced, held so the confirmation can name their login
  // id and say where their set-PIN email went. Cleared by the manager closing
  // it, never on a timer — it carries the only place that id is shown here.
  const [done, setDone] = useState(null);

  const readiness = candidate.readiness || [];
  const ready = candidate.stage === 'Ready to transfer';

  // Rebuild the form each time the dialog opens, so it starts clean rather than
  // from whatever was typed and abandoned last time. The pattern starts at the
  // usual Monday-to-Friday shape; the manager sets the real one here, which is
  // the only place it is ever asked.
  useEffect(() => {
    if (!open) return;
    setForm({
      is_carer: false,
      // Owner-only: transfer them in as a manager.
      is_manager: false,
      site_ids: [],
      app_access: false,
      medication_access: false,
      menu_ids: [],
      group_id: '',
      contracted_hours: '',
      shift_name: 'Day',
      week_pattern: defaultWeekPattern(),
      // The employment terms, asked last. Prefilled from anything already on
      // the record — a manager who set a start date while editing the candidate
      // should not find it blank here.
      employment: {
        job_title: candidate.details?.job_title || '',
        employment_type: candidate.details?.employment_type || 'Full-time',
        contract_type: candidate.details?.contract_type || 'Permanent',
        contract_end_date: toDateInput(candidate.details?.contract_end_date),
        start_date: toDateInput(candidate.details?.start_date),
        notice_period: candidate.details?.notice_period || '',
      },
    });
  }, [open, candidate.details]);

  /** One employment term. They live in their own object, sent as one block. */
  const patchEmployment = (changes) => setForm((prev) => ({
    ...prev,
    employment: { ...prev.employment, ...changes },
  }));

  const patch = (changes) => setForm((prev) => ({ ...prev, ...changes }));

  const workingDays = (form?.week_pattern || []).filter((entry) => entry.on).length;
  const paidHours = weeklyPaidHours(form?.week_pattern);

  const submit = async () => {
    // Same rule the API enforces: a carer, an app login, or dashboard modules —
    // any of them, but not none, or they have nowhere to sign in.
    if (!form.is_manager && !form.is_carer && !form.app_access && form.menu_ids.length === 0) {
      toast.error('Choose Carer, or tick mobile app access, or assign at least one dashboard module.');
      return;
    }
    if (workingDays === 0) {
      toast.error('Turn on at least one day, so they appear on the rota.');
      return;
    }
    // The same two the Add Staff form insists on, for the same reason: a staff
    // record with no job title and no start date is not a staff record anybody
    // can roster, pay or report on.
    if (!form.employment.job_title.trim()) {
      toast.error('Give them a job title — it is what the directory, rotas and payroll read.');
      return;
    }
    if (!form.employment.start_date) {
      toast.error('When do they start? The record needs a start date.');
      return;
    }

    const response = await run(() => recruitmentService.convertToStaff(candidate.id, {
      ...form,
      is_manager: isOwner && form.is_manager,
      ...(isOwner && form.is_manager && { site_ids: form.site_ids?.length ? form.site_ids : [activeSiteId] }),
      app_access: form.is_carer || form.app_access,
      medication_access: form.is_carer && form.medication_access,
      pattern_mode: domiciliary ? 'DOMICILIARY' : 'CARE_HOME',
    }));
    if (!response) return;
    setOpen(false);
    // The confirmation replaces the dialog rather than the page navigating
    // away: a transfer is irreversible and its result — the generated login id,
    // whether the email went — is worth reading before moving on.
    setDone(response.data || {});
  };

  return (
    <>
      <div className={`${cardCls} p-5`}>
        <h3 className="text-[15px] font-bold text-[#0B1220] dark:text-gray-100">Transfer to Staff Management</h3>
        <p className="text-[12.5px] text-[#64748B] dark:text-gray-400 mt-1 mb-4">
          Carry the candidate&apos;s answers across once. Set their roster, access and
          employment details here; the rest of their record is finished in Staff Management.
        </p>

        <div className="divide-y divide-[#F1F5F9] dark:divide-gray-800 border border-[#E3E9F3] dark:border-gray-700 rounded-xl overflow-hidden">
          {readiness.map((item) => (
            <div key={item.id} className="flex flex-wrap items-center justify-between gap-3 px-4 py-3.5">
              <div className="min-w-0">
                <div className="text-[13.5px] text-[#0B1220] dark:text-gray-100">{item.label}</div>
                {!item.met && item.outstanding.length > 0 && (
                  <div className="text-[12px] text-[#64748B] dark:text-gray-400 mt-0.5">
                    {item.outstanding.join(', ')}
                  </div>
                )}
              </div>
              {/* A row that does not gate the transfer reads as neutral, not as
                  a warning. References can take weeks and are somebody else's
                  homework; the three forms above are the gate. */}
              <Pill tone={item.met ? 'good' : (item.blocking === false ? 'neutral' : 'warn')}>
                {item.met ? 'Complete' : (item.blocking === false ? 'Still awaited' : 'Outstanding')}
              </Pill>
            </div>
          ))}
        </div>

        <div className="mt-4 p-4 rounded-xl bg-[#EEF3FF] dark:bg-blue-900/15 border border-[rgba(37,99,235,0.14)] flex items-start gap-2.5">
          <Info className="w-4 h-4 text-blue-600 shrink-0 mt-0.5" />
          <p className="text-[12.5px] text-[#475569] dark:text-gray-300">
            Creates a <strong className="text-[#0B1220] dark:text-gray-100">Setup pending</strong> record
            {candidate.job_title
              ? <> as <strong className="text-[#0B1220] dark:text-gray-100">{candidate.job_title}</strong></>
              : null}
            {' '}— not an active staff login. Transfer does not mean checks are complete. Outstanding
            checks remain visible for manager review.
          </p>
        </div>

        <div className="mt-4">
          <Button
            variant="primary"
            rightIcon={<ArrowRight className="w-4 h-4" />}
            disabled={locked || !ready}
            title={ready
              ? 'Set their roster and access, then create the staff record'
              : 'Finish the three forms above first'}
            onClick={() => setOpen(true)}
          >
            Convert to staff
          </Button>
        </div>
      </div>

      <ManagerChecksSection
        candidate={candidate}
        definitions={definitions}
        busy={busy}
        locked={locked}
        run={run}
      />

      <HistorySection candidate={candidate} />

      {/* ── Roster and access ───────────────────────────────────────────── */}
      <Modal
        isOpen={open && Boolean(form)}
        onClose={() => setOpen(false)}
        title={`Convert ${candidate.name} to staff`}
        maxWidth="max-w-4xl"
        footer={(
          <div className="flex justify-end gap-2.5">
            <Button variant="secondary" onClick={() => setOpen(false)}>Cancel</Button>
            <Button variant="primary" loading={busy} rightIcon={<ArrowRight className="w-4 h-4" />} onClick={submit}>
              Create staff record
            </Button>
          </div>
        )}
      >
        {form && (
          <div className="space-y-8">
            {/* ── 1. Roster ─────────────────────────────────────────────── */}
            <div>
              <div className="flex flex-wrap items-baseline justify-between gap-2 mb-1">
                <h4 className="text-[14px] font-bold text-[#0B1220] dark:text-gray-100">
                  1 · {domiciliary ? 'Availability' : 'Roster'}
                </h4>
                <span className="text-[12.5px] text-[#64748B] dark:text-gray-400 tabular-nums">
                  {workingDays} {domiciliary ? 'available' : 'working'} day{workingDays === 1 ? '' : 's'}
                  {!domiciliary && ` · ${paidHours.toFixed(1)} h/week`}
                </span>
              </div>
              <p className="text-[12.5px] text-[#64748B] dark:text-gray-400 mb-4">
                Starts from the usual pattern — set the days and times they will actually work.
              </p>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                <div>
                  <label className={fieldLabelCls} htmlFor="transfer-group">Team</label>
                  <Select
                    value={form.group_id}
                    onChange={(v) => patch({ group_id: v })}
                    options={groups.map((g) => ({ value: String(g.id), label: g.name }))}
                    placeholder="Choose a team"
                    className="w-full"
                  />
                </div>
                <div>
                  <label className={fieldLabelCls} htmlFor="transfer-hours">Contracted weekly hours</label>
                  <input
                    id="transfer-hours"
                    type="number" min="0" step="0.5"
                    className={inputCls}
                    value={form.contracted_hours}
                    placeholder="e.g. 37.5"
                    onChange={(e) => patch({ contracted_hours: e.target.value })}
                  />
                </div>
                {!domiciliary && (
                  <div>
                    <label className={fieldLabelCls} htmlFor="transfer-shift">Shift name</label>
                    <Select
                      value={form.shift_name}
                      onChange={(v) => patch({ shift_name: v })}
                      options={[
                        { value: 'Day', label: 'Day' },
                        { value: 'Night', label: 'Night' },
                        { value: 'Evening', label: 'Evening' },
                      ]}
                      className="w-full"
                    />
                  </div>
                )}
              </div>

              <WeekPatternGrid
                value={form.week_pattern}
                onChange={(week_pattern) => patch({ week_pattern })}
                domiciliary={domiciliary}
              />

              {domiciliary && (
                <p className="text-[12.5px] text-[#64748B] dark:text-gray-400 mt-3">
                  Availability is not a shift commitment — visits are assigned later in Rota.
                </p>
              )}
            </div>

            {/* ── 2. Access ─────────────────────────────────────────────── */}
            <div className="pt-6 border-t border-[#E3E9F3] dark:border-gray-800">
              <h4 className="text-[14px] font-bold text-[#0B1220] dark:text-gray-100 mb-1">2 · Access</h4>
              <p className="text-[12.5px] text-[#64748B] dark:text-gray-400 mb-4">
                This is where it is decided whether they are a carer or management — it was never
                asked when they were added, because an applicant&apos;s role can change between
                applying and being offered the job.
              </p>

              <StaffAccessPicker
                isCarer={form.is_carer}
                appAccess={form.app_access}
                medicationAccess={form.medication_access}
                menuIds={form.menu_ids}
                availableMenus={availableMenus}
                loadingMenus={loadingMenus}
                onChange={patch}
                canMakeManager={isOwner}
                isManager={form.is_manager}
                sites={sites}
                siteIds={form.site_ids?.length ? form.site_ids : [activeSiteId].filter(Boolean)}
              />
            </div>

            {/* ── 3. Employment details ─────────────────────────────────
                LAST, and not on the candidate's own form, because this is the
                EMPLOYER's side of the deal: what the job is, on what contract,
                from when. None of it is a fact about the applicant they could
                have told us earlier, and by this point the offer has been made
                so the answers are known.

                Field for field the Employment details section of Add & Edit
                Staff, writing the same columns — a transferred record and a
                typed one are the same record afterwards. It used to be left
                for Staff Management, which meant everybody arrived in the
                directory with no job title and no start date until somebody
                remembered to go back and finish it. */}
            <div className="pt-6 border-t border-[#E3E9F3] dark:border-gray-800">
              <h4 className="text-[14px] font-bold text-[#0B1220] dark:text-gray-100 mb-1">3 · Employment details</h4>
              <p className="text-[12.5px] text-[#64748B] dark:text-gray-400 mb-4">
                The employment essentials. Everything else about the job — pay, probation, line
                manager — is finished on their staff record.
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className={fieldLabelCls} htmlFor="transfer-job-title">
                    Job title <span className="text-rose-500">*</span>
                  </label>
                  {/* A list, but not a closed one — a home with a job title of
                      its own can still type it in. Same control as Add Staff. */}
                  <input
                    id="transfer-job-title"
                    list="transferJobTitles"
                    className={inputCls}
                    value={form.employment.job_title}
                    placeholder="Type or choose a job title"
                    onChange={(e) => patchEmployment({ job_title: e.target.value })}
                  />
                  <datalist id="transferJobTitles">
                    {JOB_TITLES.map((title) => <option key={title} value={title} />)}
                  </datalist>
                </div>

                <div>
                  <label className={fieldLabelCls} htmlFor="transfer-start-date">
                    Start date <span className="text-rose-500">*</span>
                  </label>
                  <input
                    id="transfer-start-date"
                    type="date"
                    className={inputCls}
                    value={form.employment.start_date}
                    onChange={(e) => patchEmployment({ start_date: e.target.value })}
                  />
                </div>

                <div>
                  <label className={fieldLabelCls}>Employment type</label>
                  <Select
                    value={form.employment.employment_type}
                    onChange={(v) => patchEmployment({ employment_type: v })}
                    options={(definitions?.employmentTypes || ['Full-time', 'Part-time'])
                      .map((value) => ({ value, label: value }))}
                    className="w-full"
                  />
                </div>

                <div>
                  <label className={fieldLabelCls}>Contract type</label>
                  <Select
                    value={form.employment.contract_type}
                    onChange={(v) => patchEmployment({
                      contract_type: v,
                      // An end date only means something on a fixed term. The
                      // API clears it too, so a stale one cannot be stored.
                      ...(v === 'Fixed-term' ? {} : { contract_end_date: '' }),
                    })}
                    options={(definitions?.contractTypes || []).map((value) => ({ value, label: value }))}
                    className="w-full"
                  />
                </div>

                {form.employment.contract_type === 'Fixed-term' && (
                  <div>
                    <label className={fieldLabelCls} htmlFor="transfer-contract-end">Contract end date</label>
                    <input
                      id="transfer-contract-end"
                      type="date"
                      className={inputCls}
                      value={form.employment.contract_end_date}
                      onChange={(e) => patchEmployment({ contract_end_date: e.target.value })}
                    />
                  </div>
                )}

                <div>
                  <label className={fieldLabelCls} htmlFor="transfer-notice">Notice period</label>
                  <input
                    id="transfer-notice"
                    className={inputCls}
                    value={form.employment.notice_period}
                    placeholder="e.g. 4 weeks"
                    onChange={(e) => patchEmployment({ notice_period: e.target.value })}
                  />
                </div>
              </div>
            </div>

            <div className="p-4 rounded-xl bg-[#F8FAFC] dark:bg-[#2c323b] border border-[#E3E9F3] dark:border-gray-700 flex items-start gap-2.5">
              <Check className="w-4 h-4 text-[#94A3B8] shrink-0 mt-0.5" />
              <p className="text-[12.5px] text-[#475569] dark:text-gray-300">
                Their login id is generated, and their documents come across with them. They set
                their own PIN from the link you send afterwards — nobody here ever sees it.
              </p>
            </div>
          </div>
        )}
      </Modal>

      {/* ── Transferred ──────────────────────────────────────────────────
          A confirmation, not a toast. Three things happened at once and each
          matters: they are staff now, their LOGIN ID was generated (this is
          one of the few places it is shown), and an email went out that is the
          only way they can set a PIN and get in. A message that disappears
          after four seconds is the wrong shape for that. */}
      <Modal
        isOpen={Boolean(done)}
        onClose={() => { const record = done; setDone(null); onTransferred?.(record); }}
        title="Transfer complete"
        maxWidth="max-w-lg"
        footer={(
          <div className="flex justify-end gap-2.5">
            <Button
              variant="primary"
              onClick={() => { const record = done; setDone(null); onTransferred?.(record); }}
            >
              Open their staff record
            </Button>
          </div>
        )}
      >
        {done && (
          <div className="space-y-4">
            <div className="flex items-start gap-3">
              <div className="w-11 h-11 rounded-2xl bg-emerald-50 dark:bg-emerald-900/20 flex items-center justify-center shrink-0">
                <Check className="w-5 h-5 text-emerald-600" />
              </div>
              <div className="min-w-0">
                <p className="text-[14px] text-[#1E293B] dark:text-gray-200">
                  <strong className="font-semibold">{done.name || candidate.name}</strong> has been
                  transferred to Staff Management.
                </p>
                <p className="text-[12.5px] text-[#64748B] dark:text-gray-400 mt-1">
                  Their recruitment record stays as it was; their documents and compliance checks
                  have come across with them.
                </p>
              </div>
            </div>

            {/* The generated login id. They never chose it and have never
                signed in, so this and their email are the only places it
                appears. */}
            {done.user_id && (
              <div className="px-4 py-3 rounded-xl bg-[#F8FAFC] dark:bg-[#2c323b] border border-[#E3E9F3] dark:border-gray-700">
                <div className={`${fieldLabelCls} mb-1`}>Login ID</div>
                <div className="text-[15px] font-bold font-mono text-[#0B1220] dark:text-gray-100">{done.user_id}</div>
              </div>
            )}

            {/* Said plainly either way. "We emailed them" when nothing was sent
                is the one thing a confirmation must never claim — the manager
                would wait for somebody who was never contacted. */}
            {done.pin_set ? (
              <p className="text-[13px] text-[#475569] dark:text-gray-300">
                They already have a PIN, so nothing was sent — they can sign in with the id above.
              </p>
            ) : done.pin_emailed ? (
              <div className="px-4 py-3 rounded-xl bg-[#EEF3FF] dark:bg-blue-900/15 border border-[rgba(37,99,235,0.14)] flex items-start gap-2.5">
                <Info className="w-4 h-4 text-blue-600 shrink-0 mt-0.5" />
                <p className="text-[12.5px] text-[#475569] dark:text-gray-300">
                  A set-password email has been sent to{' '}
                  <strong className="text-[#0B1220] dark:text-gray-100">{done.email || 'their address'}</strong>.
                  They set their own PIN from it — nobody here ever sees it. The link lasts one hour;
                  you can send another from their staff record.
                </p>
              </div>
            ) : (
              <div className="px-4 py-3 rounded-xl bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-900/40 flex items-start gap-2.5">
                <Info className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                <p className="text-[12.5px] text-[#475569] dark:text-gray-300">
                  The transfer worked, but the set-password email could not be sent. They cannot sign
                  in until it is — use <strong className="text-[#0B1220] dark:text-gray-100">Reset password</strong>{' '}
                  on their staff record to try again.
                </p>
              </div>
            )}
          </div>
        )}
      </Modal>
    </>
  );
};

export default TransferPanel;
